import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-wt-bg-primary text-wt-text-primary">
      <header className="sticky top-0 z-50 bg-wt-bg-primary/95 backdrop-blur-sm border-b border-wt-border-default">
        <div className="mx-auto max-w-4xl px-4 py-4 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <Logo variant="fullTransparent" size="h-8" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-12 lg:px-8">
        <h1 className="mb-2 font-sans text-3xl font-bold md:text-4xl">
          Watt Tower — Privacy Policy
        </h1>
        <p className="mb-12 font-sans text-sm text-wt-text-muted">
          Effective Date: February 1, 2026
        </p>

        <article className="space-y-10 font-sans text-base leading-relaxed text-wt-text-secondary">
          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              1. Overview
            </h2>
            <p className="mb-4">
              Watt Tower (“Watt Tower,” “we,” “our,” or “us”) provides energy monitoring, analytics, and advisory services for solar, battery, and hybrid energy systems.
            </p>
            <p className="mb-4">This Privacy Policy explains:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>What information we collect</li>
              <li>How we use it</li>
              <li>How it is stored and shared</li>
              <li>Your rights and choices regarding your data</li>
            </ul>
            <p>This policy applies to all users of the Watt Tower application and services.</p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              2. Information We Collect
            </h2>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">A. Account Information</h3>
            <p className="mb-4">When you create or manage an account, we may collect:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Email address</li>
              <li>Phone number (optional, for notifications)</li>
              <li>Account identifiers and authentication metadata</li>
            </ul>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">B. Energy System & Telemetry Data</h3>
            <p className="mb-4">
              To provide monitoring and analytics, we collect system-related data, which may include:
            </p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Battery state of charge (SOC)</li>
              <li>Energy production and consumption (kW, kWh)</li>
              <li>Power flow and load metrics</li>
              <li>System configuration metadata (e.g., battery capacity, inverter type)</li>
              <li>Timestamps and historical readings</li>
            </ul>
            <p className="mb-4">This data is associated with your account and monitored properties.</p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">C. Messaging & Notification Data</h3>
            <p className="mb-4">If you enable notifications, we may collect:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Phone number for SMS notifications</li>
              <li>Notification delivery status (sent, delivered, failed)</li>
              <li>Opt-in, confirmation, and opt-out timestamps</li>
              <li>Message metadata required for compliance (e.g., STOP or HELP requests)</li>
            </ul>
            <p className="mb-4">We do not use SMS data for marketing purposes.</p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">D. Usage & Technical Data</h3>
            <p className="mb-4">We automatically collect limited technical information, including:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>IP address</li>
              <li>Device and browser type</li>
              <li>App interaction events (e.g., feature usage, dismissals)</li>
              <li>Error logs and performance diagnostics</li>
            </ul>
            <p>This data is used solely to operate, secure, and improve the Service.</p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              3. How We Use Information
            </h2>
            <p className="mb-4">We use collected information to:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Provide energy monitoring, analytics, and forecasts</li>
              <li>Generate alerts and notifications you request</li>
              <li>Improve system accuracy and reliability</li>
              <li>Operate, secure, and maintain the Service</li>
              <li>Communicate service-related updates</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>
            <p>We do not sell personal data or use it for advertising.</p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              4. Messaging & Notifications
            </h2>
            <p className="mb-4">If you enable SMS or other notifications:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>You must explicitly opt in and confirm where required</li>
              <li>Messages are operational and informational only</li>
              <li>Message frequency varies based on system conditions</li>
            </ul>
            <p>
              Messaging consent, opt-out instructions, and support mechanisms are governed by the{" "}
              <Link to="/terms" className="text-wt-text-secondary underline hover:text-wt-text-primary">
                Terms of Service
              </Link>
              , which incorporate Messaging & Notification requirements.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              5. Data Sharing & Disclosure
            </h2>
            <p className="mb-4">We do not sell your personal information.</p>
            <p className="mb-4">We may share data only in the following circumstances:</p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">A. Service Providers</h3>
            <p className="mb-4">
              With trusted third-party vendors who assist in operating the Service, such as: cloud infrastructure providers; notification delivery services; monitoring and analytics providers. These providers may access data only as necessary to perform services on our behalf and are subject to confidentiality obligations.
            </p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">B. Legal & Safety Requirements</h3>
            <p className="mb-4">We may disclose information if required to:</p>
            <ul className="ml-6 list-disc space-y-2">
              <li>Comply with applicable laws or regulations</li>
              <li>Respond to lawful requests or legal process</li>
              <li>Protect the rights, safety, or security of Watt Tower, users, or the public</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              6. Data Retention
            </h2>
            <p className="mb-4">We retain information only as long as necessary to:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Provide and improve the Service</li>
              <li>Support analytics and forecasting</li>
              <li>Meet legal or regulatory requirements</li>
            </ul>
            <p>When no longer needed, data is deleted, anonymized, or aggregated as appropriate.</p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              7. Your Rights & Choices
            </h2>
            <p className="mb-4">Depending on your location, you may have the right to:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Opt out of notifications at any time</li>
            </ul>
            <p className="mb-4">
              You can manage notification preferences within the app or by following opt-out instructions in messages.
            </p>
            <p>
              To exercise data rights, contact{" "}
              <a href="mailto:support@watttower.com" className="text-wt-text-secondary underline hover:text-wt-text-primary">
                support@watttower.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              8. Data Security
            </h2>
            <p className="mb-4">
              We implement reasonable administrative, technical, and organizational safeguards to protect your data.
            </p>
            <p className="mb-4">However:</p>
            <ul className="ml-6 list-disc space-y-2">
              <li>No system is completely secure</li>
              <li>Transmission delays or failures may occur</li>
              <li>Use of the Service is at your own risk</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              9. Children’s Privacy
            </h2>
            <p>
              Watt Tower is not intended for use by individuals under the age of 18. We do not knowingly collect personal data from children.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              10. International Users
            </h2>
            <p className="mb-4">If you access Watt Tower from outside the United States:</p>
            <ul className="ml-6 list-disc space-y-2">
              <li>Your data may be processed in the United States or other jurisdictions</li>
              <li>By using the Service, you consent to such processing</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              11. Changes to This Privacy Policy
            </h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. Material changes will be communicated via email or in-app notice and reflected by an updated effective date.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              12. Contact Information
            </h2>
            <p className="mb-4">If you have questions about this Privacy Policy or our data practices:</p>
            <p className="mb-4">Watt Tower</p>
            <p>
              Email:{" "}
              <a href="mailto:support@watttower.com" className="text-wt-text-secondary underline hover:text-wt-text-primary">
                support@watttower.com
              </a>
            </p>
          </section>
        </article>

        <div className="mt-16 pt-8 border-t border-wt-border-default flex flex-wrap gap-6">
          <Link
            to="/"
            className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary transition-colors"
          >
            ← Back to home
          </Link>
          <Link
            to="/terms"
            className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary transition-colors"
          >
            Terms of Service
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
